/* eslint-disable import/no-extraneous-dependencies */
import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";

// Initial state with an empty array for assets
const initialState = { assets: [] };

// Helper function to sort assets by name
const sortAssetsByName = assetsArray => {
	return [...assetsArray].sort((a, b) =>
		(a.name || "").localeCompare(b.name || "", undefined, {
			numeric: true
		})
	);
};

const assetSlice = createSlice({
	name: "asset",
	initialState,
	reducers: {
		// Update an asset only if it has changed or doesn't exist
		updateAsset: (state, action) => {
			const index = state.assets.findIndex(asset => asset.id === action.payload.id);
			if (index === -1) {
				// Asset doesn't exist, add it
				state.assets.push(action.payload);
				state.assets = sortAssetsByName(state.assets);
			} else {
				const currentAsset = state.assets[index];
				if (JSON.stringify(currentAsset) !== JSON.stringify(action.payload)) {
					state.assets[index] = action.payload;
					state.assets = sortAssetsByName(state.assets);
				}
			}
		},
		// Delete an asset by ID
		deleteAsset: (state, action) => {
			state.assets = state.assets.filter(asset => asset.id !== action.payload);
		},
		// Add a new asset to the array and sort by name
		createAsset: (state, action) => {
			state.assets.push(action.payload);
			// Sort assets by name after creation
			state.assets = sortAssetsByName(state.assets);
		},
		createAssets: (state, action) => {
			state.assets.push(...action.payload);
			// Sort assets by name after creation
			state.assets = sortAssetsByName(state.assets);
		},
		// Replace all assets with the provided array
		setAssets: (state, action) => {
			state.assets = action.payload;
		}
	}
});

// Export the actions
export const { createAsset, createAssets, deleteAsset, updateAsset, setAssets } =
	assetSlice.actions;

// Base selector to get the assets array
export const selectAssets = state => state.knowledgeBase.asset.assets;

// Kept for backward compatibility - returns the same array
export const selectAssetsObject = selectAssets;

// Memoized selector to filter assets by folderId
export const selectAssetsByFolderId = createSelector(
	[selectAssets, (_, folderId) => folderId],
	(assets, folderId) =>
		assets
			.filter(asset => asset.folderId === folderId)
			.sort((a, b) =>
				(a.name || "").localeCompare(b.name || "", undefined, {
					numeric: true,
					sensitivity: "base"
				})
			)
);

// Memoized selector to get an asset by ID
export const selectAssetById = createSelector(
	[selectAssets, (_, assetId) => assetId],
	(assets, assetId) => assets.find(asset => asset.id === assetId) || null
);

// New memoized selector to filter assets by fileType
export const selectAssetsByAssetType = createSelector(
	[selectAssets, (_, fileType) => fileType],
	(assets, fileType) => assets.filter(asset => asset.fileType === fileType)
);

// Memoized selector to filter assets by an array of fileTypes
export const selectAssetsByAssetTypes = createSelector(
	[selectAssets, (_, fileTypes) => fileTypes],
	(assets, fileTypes) =>
		Array.isArray(fileTypes) ? assets.filter(asset => fileTypes.includes(asset.fileType)) : []
);

// Export the reducer
export default assetSlice.reducer;
